import type { CSSProperties } from "react";
import {
  ORDER_RETURN_BADGES,
  getOrderDisplayStatus,
} from "@/features/order/display";
import type {
  OperationalEnrichedOrder,
  OperationalEnrichedOrderItem,
  OperationalOrderSummary,
} from "@/features/order/types";
import { formatQuantityDisplay, getQuantityUnitLabel } from "@/lib/quantity";
import { OrderCardHeader } from "./OrderCardHeader";

type OperationalOrderCardProps = {
  summary: OperationalOrderSummary | OperationalEnrichedOrder;
  detail: OperationalEnrichedOrder | undefined;
  isExpanded: boolean;
  isLoading: boolean;
  isError: boolean;
  onToggle: () => void;
  onRetry: () => void;
};

function quantityLabel(value: number, unitType: string) {
  const unit = getQuantityUnitLabel(unitType);
  const quantity = formatQuantityDisplay(value, unitType);

  return unit ? `${quantity} ${unit}` : quantity;
}

function getReturnNote(item: OperationalEnrichedOrderItem) {
  return (
    item.return_decision_note ??
    item.return_request_note ??
    item.return_note ??
    null
  );
}

function OperationalOrderItemRow({
  item,
}: {
  item: OperationalEnrichedOrderItem;
}) {
  const returnBadge =
    item.return_status === "NONE"
      ? null
      : ORDER_RETURN_BADGES[item.return_status];
  const returnNote = getReturnNote(item);

  return (
    <div
      className="order-item-row operational-order-item-row"
      role="group"
      aria-label={`Item ${item.product_name} de ${item.supplier_name}`}
      data-testid={`order-item-row-${item.id}`}
    >
      <div className="order-item-main">
        <div className="order-item-head operational-order-item-identity">
          <div className="order-item-product-head">
            <p className="order-item-product" title={item.product_name}>
              {item.product_name}
            </p>
            {returnBadge && (
              <span
                className="order-item-return-status operational-order-item-return-status"
                style={
                  {
                    "--return-status-color": returnBadge.color,
                  } as CSSProperties
                }
              >
                <i aria-hidden="true" />
                {returnBadge.label}
              </span>
            )}
          </div>
          <div className="order-item-supplier-summary">
            <span className="order-item-supplier" title={item.supplier_name}>
              {item.supplier_name}
            </span>
          </div>
          {item.bonus_quantity > 0 && (
            <span className="operational-order-item-bonus">
              Bônus: {quantityLabel(item.bonus_quantity, item.unit_type)}
            </span>
          )}
          <span className="operational-order-item-availability">
            {item.is_available ? "Disponível" : "Indisponível"}
          </span>
          {returnNote && (
            <p className="operational-order-item-note">{returnNote}</p>
          )}
        </div>
        <div className="order-item-meta operational-order-item-quantities">
          <span className="operational-order-item-quantity">
            <span>Solicitado</span>
            <strong>{quantityLabel(item.quantity, item.unit_type)}</strong>
          </span>
          {item.confirmed_quantity != null && (
            <span className="operational-order-item-quantity">
              <span>Confirmado</span>
              <strong>
                {quantityLabel(item.confirmed_quantity, item.unit_type)}
              </strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function OperationalOrderCardDetails({
  summary,
  detail,
  isLoading,
  isError,
  onRetry,
}: Omit<OperationalOrderCardProps, "isExpanded" | "onToggle">) {
  return (
    <section
      id={`order-card-panel-${summary.id}`}
      data-testid={`order-card-details-${summary.id}`}
      className="order-card-details"
    >
      {isLoading && (
        <p className="order-card-state" role="status">
          Carregando itens...
        </p>
      )}
      {isError && !isLoading && (
        <div className="order-card-state" role="alert">
          <p>Pedido indisponível</p>
          <button type="button" onClick={onRetry}>
            Tentar novamente
          </button>
        </div>
      )}
      {!isLoading && !isError && !detail && (
        <p className="order-card-state">Aguardando itens...</p>
      )}
      {!isLoading && !isError && detail?.items.length === 0 && (
        <p className="order-card-state">Sem itens neste pedido</p>
      )}
      {!isLoading && !isError && detail && detail.items.length > 0 && (
        <div className="order-card-item-list">
          {detail.items.map((item) => (
            <OperationalOrderItemRow key={item.id} item={item} />
          ))}
        </div>
      )}
    </section>
  );
}

export function OperationalOrderCard({
  summary,
  detail,
  isExpanded,
  isLoading,
  isError,
  onToggle,
  onRetry,
}: OperationalOrderCardProps) {
  const { color: accentColor } = getOrderDisplayStatus(summary);

  return (
    <article
      className="order-card"
      data-testid={`order-card-${summary.id}`}
      data-expanded={isExpanded ? "true" : "false"}
      style={{ "--order-card-accent": accentColor } as CSSProperties}
    >
      <OrderCardHeader
        order={summary}
        isSuper
        expanded={isExpanded}
        totalPrice={undefined}
        onToggle={onToggle}
      />
      {isExpanded && (
        <OperationalOrderCardDetails
          summary={summary}
          detail={detail}
          isLoading={isLoading}
          isError={isError}
          onRetry={onRetry}
        />
      )}
    </article>
  );
}
